import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { getUserAlerts } from '../../services/propertyAlertsService';
import { wishlistService } from '../../services/wishlistService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

const formatPrice = (price) => {
  if (!price) return 'Prix sur demande';
  if (price >= 1000000) return `${(price / 1000000)?.toFixed(1)?.replace(/\.0$/, '')}M XOF`;
  if (price >= 1000) return `${(price / 1000)?.toFixed(0)}K XOF`;
  return `${price?.toLocaleString()} XOF`;
};

const EVENT_TYPES = {
  inquiry: { label: 'Demande envoyée', icon: 'MessageSquare', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
  viewing: { label: 'Visite planifiée', icon: 'Calendar', color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100' },
  saved: { label: 'Propriété sauvegardée', icon: 'Heart', color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100' },
  alert: { label: 'Alerte déclenchée', icon: 'Bell', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
};

const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-100',
  pending: 'bg-amber-50 text-amber-700 border-amber-100',
  scheduled: 'bg-violet-50 text-violet-700 border-violet-100',
  confirmed: 'bg-green-50 text-green-700 border-green-100',
  completed: 'bg-surface text-text-secondary border-border',
  cancelled: 'bg-red-50 text-red-600 border-red-100',
  rescheduled: 'bg-orange-50 text-orange-700 border-orange-100',
  active: 'bg-green-50 text-green-700 border-green-100',
  inactive: 'bg-surface text-text-secondary border-border',
};

const STATUS_LABELS = {
  new: 'Nouveau', pending: 'En attente', scheduled: 'Planifié',
  confirmed: 'Confirmé', completed: 'Terminé', cancelled: 'Annulé',
  rescheduled: 'Reprogrammé', active: 'Actif', inactive: 'Inactif',
};

// ─── Activity History Page ─────────────────────────────────────────────────────

const ActivityHistoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Stats
  const [stats, setStats] = useState({ inquiries: 0, viewings: 0, saved: 0, alerts: 0 });

  // ─── Data Loading ────────────────────────────────────────────────────────────

  const loadAllActivity = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    try {
      const [inquiriesRes, appointmentsRes, wishlistRes, alertsRes] = await Promise.all([
        supabase
          ?.from('property_inquiries')
          ?.select('*, property:properties(id, title, address, property_type)')
          ?.eq('client_id', user?.id)
          ?.order('created_at', { ascending: false }),
        supabase
          ?.from('appointments')
          ?.select('*, property:properties(id, title, address, property_type)')
          ?.eq('client_id', user?.id)
          ?.order('scheduled_date', { ascending: false }),
        wishlistService?.getWishlist(user?.id, {}),
        getUserAlerts(user?.id),
      ]);

      const allEvents = [];

      // Inquiries
      const inquiries = inquiriesRes?.data || [];
      inquiries?.forEach(item => {
        allEvents?.push({
          id: `inquiry-${item?.id}`,
          type: 'inquiry',
          date: item?.created_at,
          title: item?.property?.title || 'Propriété',
          subtitle: item?.message?.substring(0, 80) + (item?.message?.length > 80 ? '…' : ''),
          status: item?.status || 'new',
          propertyId: item?.property_id,
          address: item?.property?.address,
          raw: item,
        });
      });

      // Appointments / Viewings
      const appointments = appointmentsRes?.data || [];
      appointments?.forEach(item => {
        allEvents?.push({
          id: `viewing-${item?.id}`,
          type: 'viewing',
          date: item?.scheduled_date || item?.created_at,
          title: item?.property?.title || item?.appointment_type || 'Rendez-vous',
          subtitle: item?.notes || item?.property?.address || '',
          status: item?.status || 'scheduled',
          propertyId: item?.property_id,
          address: item?.property?.address,
          raw: item,
        });
      });

      // Saved / Wishlist
      const wishlistItems = wishlistRes?.data || [];
      wishlistItems?.forEach(item => {
        allEvents?.push({
          id: `saved-${item?.id || item?.property_id}`,
          type: 'saved',
          date: item?.created_at || item?.added_at,
          title: item?.property?.title || item?.title || 'Propriété sauvegardée',
          subtitle: item?.property?.address || item?.address || '',
          status: 'active',
          propertyId: item?.property_id,
          address: item?.property?.address || item?.address,
          price: item?.property?.price || item?.price,
          raw: item,
        });
      });

      // Alerts
      const alertItems = alertsRes?.data || [];
      alertItems?.forEach(item => {
        allEvents?.push({
          id: `alert-${item?.id}`,
          type: 'alert',
          date: item?.created_at,
          title: item?.name || 'Alerte propriété',
          subtitle: [
            item?.commune_names?.join(', '),
            item?.property_types?.join(', '),
            item?.min_price ? `Min: ${formatPrice(item?.min_price)}` : null,
            item?.max_price ? `Max: ${formatPrice(item?.max_price)}` : null,
          ]?.filter(Boolean)?.join(' · '),
          status: item?.is_active ? 'active' : 'inactive',
          raw: item,
        });
      });

      // Sort all events by date descending
      allEvents?.sort((a, b) => new Date(b?.date) - new Date(a?.date));

      setEvents(allEvents);
      setStats({
        inquiries: inquiries?.length,
        viewings: appointments?.length,
        saved: wishlistItems?.length,
        alerts: alertItems?.length,
      });
    } catch (err) {
      console.error(err);
      setError('Impossible de charger l\'historique d\'activité.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadAllActivity(); }, [loadAllActivity]);

  // ─── Filtering ───────────────────────────────────────────────────────────────

  const filteredEvents = useMemo(() => {
    let result = [...events];

    if (filterType !== 'all') {
      result = result?.filter(e => e?.type === filterType);
    }

    if (filterPeriod !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      if (filterPeriod === '7d') cutoff?.setDate(now?.getDate() - 7);
      else if (filterPeriod === '30d') cutoff?.setDate(now?.getDate() - 30);
      else if (filterPeriod === '90d') cutoff?.setDate(now?.getDate() - 90);
      result = result?.filter(e => new Date(e?.date) >= cutoff);
    }

    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      result = result?.filter(e =>
        e?.title?.toLowerCase()?.includes(q) ||
        e?.subtitle?.toLowerCase()?.includes(q) ||
        e?.address?.toLowerCase()?.includes(q)
      );
    }

    return result;
  }, [events, filterType, filterPeriod, searchQuery]);

  // ─── Export ──────────────────────────────────────────────────────────────────

  const handleExportCSV = () => {
    const headers = ['Type', 'Date', 'Titre', 'Détails', 'Statut', 'Adresse'];
    const rows = filteredEvents?.map(e => [
      EVENT_TYPES?.[e?.type]?.label || e?.type,
      formatDate(e?.date),
      `"${(e?.title || '')?.replace(/"/g, '""')}"`,
      `"${(e?.subtitle || '')?.replace(/"/g, '""')}"`,
      STATUS_LABELS?.[e?.status] || e?.status,
      `"${(e?.address || '')?.replace(/"/g, '""')}"`,
    ]);

    const csvContent = [headers?.join(','), ...rows?.map(r => r?.join(','))]?.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `historique-activite-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL?.revokeObjectURL(url);
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (!user) {
    return (
      <div className="min-h-screen bg-background font-system">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 bg-surface rounded-2xl flex items-center justify-center mb-4 shadow-ios-light">
            <Icon name="Lock" size={28} color="var(--color-brand-earth)" strokeWidth={1.5} />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">Connexion requise</h2>
          <p className="text-ios-body font-system text-text-secondary text-center mb-6 max-w-sm">
            Connectez-vous pour accéder à votre historique d'activité.
          </p>
          <Link to="/auth">
            <button className="px-6 py-3 bg-brand-earth text-white rounded-xl font-system font-body-medium text-ios-callout shadow-ios-light hover:opacity-90 transition-opacity">
              Se connecter
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-system">
      <Helmet>
        <title>Historique d'Activité — Kapital States</title>
        <meta name="description" content="Consultez vos demandes, visites, propriétés sauvegardées et alertes." />
      </Helmet>

      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-surface/70 transition-colors"
              aria-label="Retour"
            >
              <Icon name="ChevronLeft" size={20} color="var(--color-text-secondary)" strokeWidth={2} />
            </button>
            <h1 className="text-ios-title1 font-display font-display-semibold text-brand-earth tracking-tight">
              Historique d'Activité
            </h1>
          </div>
          <p className="text-ios-callout font-system text-text-secondary ml-11">
            Toutes vos interactions sur Kapital States
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { key: 'inquiries', label: 'Demandes', icon: 'MessageSquare', value: stats?.inquiries, color: 'text-blue-600', bg: 'bg-blue-50' },
            { key: 'viewings', label: 'Visites', icon: 'Calendar', value: stats?.viewings, color: 'text-violet-600', bg: 'bg-violet-50' },
            { key: 'saved', label: 'Sauvegardées', icon: 'Heart', value: stats?.saved, color: 'text-rose-600', bg: 'bg-rose-50' },
            { key: 'alerts', label: 'Alertes', icon: 'Bell', value: stats?.alerts, color: 'text-amber-600', bg: 'bg-amber-50' },
          ]?.map(stat => (
            <button
              key={stat?.key}
              onClick={() => setFilterType(filterType === stat?.key ? 'all' : stat?.key)}
              className={`p-4 rounded-2xl border transition-all duration-200 text-left ${
                filterType === stat?.key
                  ? 'border-brand-gold shadow-ios-medium bg-white': 'border-border bg-white shadow-ios-light hover:shadow-ios-medium'
              }`}
            >
              <div className={`w-9 h-9 ${stat?.bg} rounded-xl flex items-center justify-center mb-3`}>
                <Icon name={stat?.icon} size={18} className={stat?.color} strokeWidth={2} />
              </div>
              <div className="text-ios-title2 font-display font-display-semibold text-brand-earth leading-none mb-1">
                {loading ? '—' : stat?.value}
              </div>
              <div className="text-ios-caption1 font-system text-text-secondary">{stat?.label}</div>
            </button>
          ))}
        </div>

        {/* Filters + Export Bar */}
        <div className="bg-white rounded-2xl border border-border shadow-ios-light p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" strokeWidth={2} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                placeholder="Rechercher dans l'historique…"
                className="w-full pl-9 pr-4 py-2.5 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none transition-all"
              />
            </div>

            {/* Period Filter */}
            <select
              value={filterPeriod}
              onChange={e => setFilterPeriod(e?.target?.value)}
              className="px-4 py-2.5 border border-border rounded-xl text-ios-callout font-system bg-white focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none cursor-pointer"
            >
              <option value="all">Toute la période</option>
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="90d">90 derniers jours</option>
            </select>

            {/* Export Button */}
            <button
              onClick={handleExportCSV}
              disabled={filteredEvents?.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-medium shadow-ios-light hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <Icon name="Download" size={16} strokeWidth={2} />
              Exporter CSV
            </button>
          </div>

          {/* Type Filter Chips */}
          <div className="flex flex-wrap gap-2 mt-3">
            {[
              { key: 'all', label: 'Tout', icon: 'LayoutGrid' },
              { key: 'inquiry', label: 'Demandes', icon: 'MessageSquare' },
              { key: 'viewing', label: 'Visites', icon: 'Calendar' },
              { key: 'saved', label: 'Sauvegardées', icon: 'Heart' },
              { key: 'alert', label: 'Alertes', icon: 'Bell' },
            ]?.map(chip => (
              <button
                key={chip?.key}
                onClick={() => setFilterType(chip?.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-ios-footnote font-system font-body-medium transition-all duration-200 ${
                  filterType === chip?.key
                    ? 'bg-brand-earth text-white shadow-ios-light'
                    : 'bg-surface text-text-secondary hover:bg-surface/80 hover:text-text-primary'
                }`}
              >
                <Icon name={chip?.icon} size={13} strokeWidth={2} />
                {chip?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-ios-footnote font-system text-text-secondary mb-4 px-1">
            {filteredEvents?.length} événement{filteredEvents?.length !== 1 ? 's' : ''} affiché{filteredEvents?.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Timeline */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
            <Icon name="AlertCircle" size={28} className="text-red-500 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-ios-callout font-system text-red-700">{error}</p>
            <button onClick={loadAllActivity} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl text-ios-footnote font-system font-body-medium hover:opacity-90 transition-opacity">
              Réessayer
            </button>
          </div>
        ) : filteredEvents?.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-12 text-center shadow-ios-light">
            <div className="w-16 h-16 bg-surface rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="ClipboardList" size={28} color="var(--color-text-secondary)" strokeWidth={1.5} />
            </div>
            <h3 className="text-ios-title3 font-display font-display-semibold text-brand-earth mb-2">Aucune activité</h3>
            <p className="text-ios-callout font-system text-text-secondary max-w-xs mx-auto">
              {filterType !== 'all' || filterPeriod !== 'all' || searchQuery ?'Aucun résultat pour ces filtres. Essayez d\'élargir votre recherche.' :'Votre historique d\'activité apparaîtra ici dès que vous interagirez avec des propriétés.'}
            </p>
            {(filterType !== 'all' || filterPeriod !== 'all' || searchQuery) && (
              <button
                onClick={() => { setFilterType('all'); setFilterPeriod('all'); setSearchQuery(''); }}
                className="mt-4 px-4 py-2 bg-brand-earth text-white rounded-xl text-ios-footnote font-system font-body-medium hover:opacity-90 transition-opacity"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEvents?.map((event, idx) => {
              const typeConfig = EVENT_TYPES?.[event?.type];
              return (
                <div
                  key={event?.id}
                  className="bg-white border border-border rounded-2xl p-4 shadow-ios-light hover:shadow-ios-medium transition-all duration-200 group"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 ${typeConfig?.bg} rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon name={typeConfig?.icon} size={18} className={typeConfig?.color} strokeWidth={2} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className={`text-ios-caption1 font-system font-body-medium px-2 py-0.5 rounded-full border ${typeConfig?.bg} ${typeConfig?.color} ${typeConfig?.border}`}>
                              {typeConfig?.label}
                            </span>
                            <span className={`text-ios-caption1 font-system font-body-medium px-2 py-0.5 rounded-full border ${STATUS_COLORS?.[event?.status] || 'bg-surface text-text-secondary border-border'}`}>
                              {STATUS_LABELS?.[event?.status] || event?.status}
                            </span>
                          </div>
                          <h3 className="text-ios-callout font-system font-body-semibold text-text-primary truncate mt-1">
                            {event?.title}
                          </h3>
                          {event?.subtitle && (
                            <p className="text-ios-footnote font-system text-text-secondary mt-0.5 line-clamp-2">
                              {event?.subtitle}
                            </p>
                          )}
                          {event?.address && (
                            <div className="flex items-center gap-1 mt-1.5">
                              <Icon name="MapPin" size={12} className="text-text-secondary flex-shrink-0" strokeWidth={2} />
                              <span className="text-ios-caption1 font-system text-text-secondary truncate">{event?.address}</span>
                            </div>
                          )}
                        </div>

                        {/* Date + Action */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <span className="text-ios-caption1 font-system text-text-secondary whitespace-nowrap">
                            {formatDateShort(event?.date)}
                          </span>
                          {event?.propertyId && (
                            <Link
                              to={`/property/${event?.propertyId}`}
                              className="flex items-center gap-1 text-ios-caption1 font-system font-body-medium text-brand-earth hover:underline"
                            >
                              Voir
                              <Icon name="ArrowRight" size={12} strokeWidth={2} />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom padding */}
        <div className="h-12" />
      </main>
    </div>
  );
};

export default ActivityHistoryPage;
